import { Button, Card, Intent } from "@blueprintjs/core"
import CustomSpinner from "components/Atoms/CustomSpinner"
import StringInput from "components/Atoms/StringInput"
import Error from "components/Common/Error"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import FormController from "components/Molecules/FormController"
import MutationOptions from "components/Organisms/MutationOptions"
import { useCompanyDetailLazyQuery } from "generated/graphql"
import { ADD_UPDATE_COMPANY, COMPANY_LIST } from "graphql/company.graphql.gql"
import { ALL_DEPARTMENTS } from "graphql/selector.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import useErrors from "hooks/useErrors"
import { cloneDeep, get, set, update } from "lodash-es"
import companyModel from "models/atoms/company.model"
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppToaster from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import { graphqlError } from "utils/parseError"

const CompanyRegistration = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const idNumber = parseInt(id)
  const [company, setCompany] = useState<any>()
  const [getCompanyDetails, { loading, error }] = useCompanyDetailLazyQuery({
    onCompleted({ companyDetails }) {
      setCompany(companyDetails)
    },
  })
  useEffect(() => {
    if (idNumber) {
      getCompanyDetails({ variables: { id: idNumber } })
    } else {
      setCompany({})
    }
    return function cleanup() {
      setCompany({})
    }
  }, [idNumber, getCompanyDetails])
  // const [company, setCompany] = useState(company);
  const [errors] = useErrors({ schema: companyModel, state: company })
  const isUpdating = parseInt(id) ? true : false

  function handleChange(path, value) {
    const companyDetailsCopy = { ...company }
    set(companyDetailsCopy, path, value)
    setCompany(companyDetailsCopy)
  }

  function showToast(data) {
    const name = get(data, "addCompany.company.name")
    AppToaster.show({
      message: `Company ${name} ${
        isUpdating ? "Updated" : "Added"
      } successfully`,
      intent: "success",
    })
  }

  const parsedVariables = useMemo(() => {
    const companyCopy = cloneDeep(company)
    update(companyCopy, "departments", d => d?.map(getId))
    return companyCopy
  }, [company])
  const disabled = useFormDisabled("company", company)
  if (loading) return <CustomSpinner />
  if (error) return <Error message={error.message} />

  return (
    <Card className="vt-crop-registration">
      <div className="mt-2 w-full flex items-center">
        <span>
          <Button
            icon="arrow-left"
            onClick={() => navigate(-1)}
            minimal={true}
          />
        </span>
        <h5 className="ml-3">{isUpdating ? "Edit" : "New"} Company</h5>
      </div>
      <div className="container mt-3">
        <FormController
          source={company}
          errors={errors.details}
          onChange={handleChange}
          disabled={disabled}
          disableAll={true}
        >
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <StringInput
                big={true}
                label="Company Name"
                helperText="eg. Sahyadri Farmer Producer Company Limited"
                path="name"
              />
            </div>
            <div className="col-span-4">
              <StringInput
                big={true}
                label="Codename"
                helperText="eg. 1234a64"
                path="codename"
              />
            </div>
            <div className="col-span-4">
              <StringInput
                big={true}
                label="Display Name"
                path="displayName"
                helperText="eg. SFPCL"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4 mt-3">
            <div className="col-span-4">
              <DynamicMultiSelector
                fill
                query={ALL_DEPARTMENTS}
                label="Departments"
                big={true}
                path="departments"
              ></DynamicMultiSelector>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4" />
        </FormController>
        <MutationOptions
          disabled={disabled}
          validate={() => {
            if (errors.latest) {
              AppToaster.show({
                message: errors.latest,
                intent: "danger",
                icon: "cross",
              })
              return false
            }
            return true
          }}
          refetchQueries={[
            {
              query: COMPANY_LIST,
            },
          ]}
          mutation={ADD_UPDATE_COMPANY}
          variables={parsedVariables}
          onError={err => {
            AppToaster.show({
              message: graphqlError(err.message),
              intent: Intent.DANGER,
            })
          }}
          onSave={data => {
            showToast(data)
            navigate(-1)
          }}
          onSaveAndEdit={data => {
            showToast(data)
            const companyId = data?.addCompany?.company?.id
            if (!parseInt(id))
              navigate(`/d/registration/company/${companyId}`, {
                replace: true,
              })
          }}
          onSaveAndAdd={data => {
            showToast(data)
            setCompany({ departments: [] })
          }}
        />
      </div>
    </Card>
  )
}

export default CompanyRegistration
